import { ChangeDetectionStrategy, Component, HostListener, Input, signal, Type } from '@angular/core';
import { PostItem } from '@app/core/models';
import { EclassWorkerEnum } from '@app/core/models/eclass-worker.enum';
import { SHARED_MODULES } from '@app/shared';
import { Observable } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { ActivityItem } from '../post-activity/activity';
import { AnnouncementItem } from '../post-announcement/announcement';
import { DailyItem } from '../post-daily/daily';
import { CPBProvaItem } from '../post-exam/cpbprova';

export type LoaderFn = (fn: any) => void;
export type Options<T> = {
  icon: string;
  title: string;
  link?: string;
  class?: string;
  action?: (item: T) => Observable<T> | null;
};

export type WorkerConfig<T = PostItem> = {
  icon?: string;
  singular?: string;
  plural?: string;
  worker?: EclassWorkerEnum;
  color: string;
  iconStyle?: string;
  labelStyle?: string;
  subtitle: Type<any>;
  extra?: Type<any>;
  action: (item: T) => Observable<T>;
  options: (item: T) => Observable<Options<T>[]>;
};

export type ItemFn = () => WorkerConfig<any>;

type WorkerMap = {
  [key in EclassWorkerEnum]?: WorkerConfig<any>;
};

@Component({
  selector: 'app-item-post',
  imports: [SHARED_MODULES],
  templateUrl: './item-post.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemPostComponent {
  public readonly EclassWorkerEnum = EclassWorkerEnum;

  protected readonly clicked = signal(false);
  protected readonly showMenu = signal(false);
  protected readonly postItem = signal<PostItem>(null);
  protected readonly config = signal<WorkerConfig<any>>(null);
  private readonly _config = signal<WorkerConfig<any>>(null);
  private static openedMenu: ItemPostComponent | null = null;

  private readonly workerMap: WorkerMap = {
    [EclassWorkerEnum.Daily]: DailyItem(),
    [EclassWorkerEnum.Activity]: ActivityItem(),
    [EclassWorkerEnum.CpbProvas]: CPBProvaItem(),
    [EclassWorkerEnum.Announcement]: AnnouncementItem(),
  };

  @Input()
  public set item(post: PostItem) {
    this.postItem.set(post);

    if (!this._config()) {
      this.config.set(this.workerMap[post.worker_id]);
    }
  }

  @HostListener('document:click')
  public onClickOutside() {
    this.closeMenu();
  }

  protected onToogle(event: MouseEvent) {
    event.stopPropagation();

    if (ItemPostComponent.openedMenu && ItemPostComponent.openedMenu !== this) {
      ItemPostComponent.openedMenu.closeMenu();
    }

    this.showMenu.update((status) => {
      ItemPostComponent.openedMenu = !status ? this : null;

      return !status;
    });
  }

  protected isPreviousYear(date: string | Date): boolean {
    return new Date(date).getFullYear() < new Date().getFullYear();
  }

  private closeMenu(): void {
    this.showMenu.set(false);

    if (ItemPostComponent.openedMenu === this) {
      ItemPostComponent.openedMenu = null;
    }
  }

  public loaderFn = (fn: any) => {
    this.clicked.set(true);
    fn()
      .pipe(
        take(1),
        finalize(() => this.clicked.set(false)),
      )
      .subscribe();
  };

  public onAction = () => {
    if (!this.clicked()) {
      this.clicked.set(true);

      this.config()
        .action(this.postItem())
        .pipe(
          take(1),
          finalize(() => this.clicked.set(false)),
        )
        .subscribe();
    }
  };

  public onOption(option: Options<PostItem>) {
    if (!this.clicked()) {
      this.clicked.set(true);
      const res = option.action(this.postItem());

      if (!!res) {
        res
          .pipe(
            take(1),
            finalize(() => this.clicked.set(false)),
          )
          .subscribe();
      } else {
        this.clicked.set(false);
      }
    }
  }
}
